<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import SancarePasswordInput from '../design-system/atoms/SancarePasswordInput.vue'

const props = defineProps<{ ssoLoginUrl?: string }>()

const store = useStore()
const router = useRouter()
const route = useRoute()

const username = defineModel<string>('username', { default: '' })
const password = defineModel<string>('password', { default: '' })
const authRequest = computed(() => store.state.login.authRequest)
const expiredPassword = computed(() => store.state.login.expiredPassword)
const usernameInput = ref<HTMLInputElement | null>(null)
// The same frontend build serves every facility, and most have no Authentik relay: only the backend knows whether SSO exists here.
const ssoEnabled = ref(false)

watch(expiredPassword, () => {
  expirePasswordcheck()
})

onMounted(() => {
  expirePasswordcheck()
  store.commit('authRequestReset')
  usernameInput.value?.focus()
  checkSsoEnabled()
})

async function checkSsoEnabled() {
  if (!props.ssoLoginUrl) {
    return
  }

  // TODO BUILD-898: use the Vuex-free api.ts once available (doNotRetry + cannotBeDelayed, no error toast, see https://app.clickup.com/t/2628858/BUILD-898)
  try {
    const response = await fetch('/api/oidc/status')
    ssoEnabled.value = response.ok && (await response.json()).enabled === true
  } catch {
    // Password login still works, so a failed probe hides the button rather than breaking the page.
    ssoEnabled.value = false
  }
}

function expirePasswordcheck() {
  if (!expiredPassword.value) {
    return
  }

  const newRoute = {
    path: '/login/expired-password',
    query: { username: username.value },
  }

  store.commit('authRequestReset')

  router.push(newRoute)
}

function handleSubmit() {
  store.dispatch('authenticate', { password: password.value, username: username.value })
}

function handleSsoLogin() {
  const url = new URL(props.ssoLoginUrl as string, window.location.origin)
  if (typeof route.query.redirect === 'string') {
    url.searchParams.set('redirect', route.query.redirect)
  }
  window.location.href = url.toString()
}


</script>

<template>
  <div>
    <form
      name="login-form"
      @submit.prevent="handleSubmit"
    >
      <h1 class="unlogged-page-title">
        Authentification
      </h1>
      <div class="mb-3">
        <span
          class="small-label"
          for="username"
        >
          Identifiant
        </span>
        <input
          ref="usernameInput"
          v-model="username"
          name="username"
          class="form-control login-input"
        >
      </div>
      <div class="mb-3">
        <label
          class="small-label"
          for="password"
        >
          Password
        </label>
        <sancare-password-input
          v-model="password"
          name="password"
          class="login-input"
        />
      </div>
      <div class="text-end d-grid">
        <button
          name="connect"
          class="btn btn-push btn-primary btn-padded"
          type="submit"
        >
          Valider
        </button>
      </div>
      <div
        v-if="authRequest.error"
        class="unlogged-page-error-label"
      >
        {{ authRequest.error }}
      </div>
      <div
        v-if="ssoLoginUrl && ssoEnabled"
        class="my-4"
      >
        <div class="d-flex gap-3 align-items-center mb-4">
          <hr class="border-top border-dark flex-grow-1">
          <span>ou connectez-vous avec</span>
          <hr class="border-top border-dark flex-grow-1">
        </div>
        <div class="d-grid">
          <button
            name="sso-login"
            class="btn btn-push btn-secondary btn-padded shadow-sm"
            type="button"
            @click="handleSsoLogin"
          >
            <!-- Authentik logo -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="30"
              width="30"
              viewBox="0 0 800 700"
              aria-hidden="true"
            >
              <path
                d="M594.05,38.06h-289.87c-63.46,0-115.37,51.92-115.37,115.37v289.87c0,63.46,51.92,115.37,115.37,115.37h34.2v-112.93h221.49v112.93h34.18c63.46,0,115.37-51.92,115.37-115.37V153.43c0-63.46-51.92-115.37-115.37-115.37ZM543.82,354H188.99v-78.04h183.33v-108.36h29.77v64.55h17.45v-64.55h21.05v47.47h17.45v-47.47h21.05v79.3h17.45v-79.3h29.77v108.36h17.51v78.04Z"
                fill="#fd4b2d"
              />
              <path
                d="M190.32,271.3h-.01c-17.27-23.36-43.16-47.14-71.85-47.14-32.99.02-63.31,18.12-79,47.14-32.77,57.15,12.53,134.23,79,132.48,51.43,0,93.85-76.31,93.85-89.8,0-5.97-8.3-24.19-21.98-42.68ZM81.24,271.3c18.51-15.97,45.39-20.63,64.82,0h.01c14.66,13.3,27.46,32.66,32.09,42.5-57.66,120.57-158.73,17.81-96.93-42.5Z"
                fill="#fd4b2d"
              />
            </svg>
            <span class="d-inline-block ms-1">Authentik</span>
          </button>
        </div>
      </div>
    </form>
  </div>
</template>
